// Upload a business document, or edit the details of one already stored.
//
// A document IS its file, so on ADD the file is required and the upload is the
// save. On EDIT the file is fixed: replacing it would silently change what a
// "signed agreement" points at while keeping its title and date, which is exactly
// the confusion this screen exists to avoid. Re-uploading is the honest path, and
// the hint says so.
import { useState } from 'react';
import { useLookups } from '../../context/LookupsContext';
import { documentsApi } from '../../api';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import { Field, Textarea } from '../../components/Form';
import { DateControl } from '../../components/DateField';
import { ErrorText } from '../../components/Loader';
import CategorySelect from './CategorySelect';

export default function DocumentModal({ document, onClose, onSaved }) {
  const lookups = useLookups();
  const isNew = !document.id;
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: document.title || '',
    // A new upload lands in whatever drawer is already filtered on screen, or the
    // first active one — never blank, so the common case is one less decision.
    category: document.category || (lookups.docCategoryOptions[0] || {}).value || '',
    doc_date: document.doc_date || '',
    notes: document.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        title: form.title.trim(),
        category: form.category,
        doc_date: form.doc_date || '',
        notes: form.notes || '',
      };
      if (isNew) await documentsApi.create(file, body);
      else await documentsApi.update(document.id, body);
      onSaved();
    } catch (e) {
      setError(e);
      setBusy(false); // stay open so the file selection isn't lost on a failure
    }
  };

  return (
    <Modal
      title={isNew ? he.documents.add : he.documents.edit}
      onClose={onClose}
      onSave={submit}
      saveLabel={busy ? he.documents.uploading : he.common.save}
      saveDisabled={busy || !form.category || (isNew && !file)}
    >
      {isNew ? (
        <Field label={`${he.documents.file} *`}>
          <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
        </Field>
      ) : (
        <Field label={he.documents.file}>
          <span className="file-name" title={document.original_name}>{document.original_name}</span>
        </Field>
      )}

      <Field label={he.documents.name}>
        <input
          value={form.title}
          onChange={set('title')}
          placeholder={he.documents.namePlaceholder}
        />
      </Field>

      <CategorySelect
        value={form.category}
        onChange={(code) => setForm({ ...form, category: code })}
        // On edit, keep a now-inactive drawer in the list so saving cannot silently
        // re-file a document that is sitting in one.
        options={lookups.docCategoryOptionsFor(document.category)}
      />

      <Field label={he.documents.date}>
        <DateControl label={he.documents.date} value={form.doc_date} onChange={set('doc_date')} />
      </Field>

      <Textarea label={he.documents.notes} value={form.notes} onChange={set('notes')} />

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
